"use client"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { ArrowRightMini, XMark } from "@medusajs/icons"
import { Text, clx, useToggleState } from "@medusajs/ui"
import { Fragment, useState } from "react"

import LocalizedClientLink from "@modules/common/components/localized-client-link"
import TileSwatch from "@modules/common/components/tile-swatch"
import SearchBox from "@modules/layout/components/search-box"
import CountrySelect from "../country-select"
import LanguageSelect from "../language-select"
import { HttpTypes } from "@medusajs/types"
import { Locale } from "@lib/data/locales"

// Las 7 categorías reales — `look`/`color`/`color2` alimentan el TileSwatch.
const LOOK_CATEGORIES = [
  { label: "Subway", handle: "subway", look: "subway", color: "#F1ECE2", color2: "#DAD0BF" },
  { label: "Marble Look", handle: "marble-look", look: "marble", color: "#E9E3D6", color2: "#FBF7F0" },
  { label: "Hexagon", handle: "hexagon", look: "hexagon", color: "#F0E7D8", color2: "#D7C9B2" },
  { label: "Zellige", handle: "zellige", look: "zellige", color: "#5F6B53", color2: "#7C8A6C" },
  { label: "Terrazzo", handle: "terrazzo", look: "terrazzo", color: "#EFE9DC", color2: "#FBF7F0" },
  { label: "Penny Round", handle: "penny-round", look: "penny", color: "#EDE6D8", color2: "#D2C4AC" },
  { label: "Herringbone", handle: "herringbone", look: "herringbone", color: "#EAE3D4", color2: "#C9A678" },
]

// Chips de color → /store?color=<Name>
const COLOR_CHIPS = [
  { label: "White", swatch: "#FBF7F0", ring: "#E6DDCD" },
  { label: "Beige & Cream", swatch: "#E9DEC8", ring: "#D8CBB0" },
  { label: "Black & White", swatch: "#211C16", ring: "#211C16", split: "#FBF7F0" },
  { label: "Terracotta", swatch: "#B5562F", ring: "#8E3F20" },
  { label: "Green", swatch: "#5F6B53", ring: "#48523F" },
  { label: "Gray", swatch: "#9A9388", ring: "#7A6F61" },
  { label: "Blue", swatch: "#5B7184", ring: "#3F5360" },
]

// Links de primer nivel (destinos honestos existentes)
const TopLinks: { name: string; href: string; accent?: boolean }[] = [
  { name: "Shop all", href: "/store" },
  { name: "Samples", href: "/samples", accent: true },
  { name: "For the Trade", href: "/trade" },
  { name: "How It Works", href: "/how-it-works" },
  { name: "Account", href: "/account" },
  { name: "Cart", href: "/cart" },
]

type SideMenuProps = {
  regions: HttpTypes.StoreRegion[] | null
  locales: Locale[] | null
  currentLocale: string | null
}

// Sección colapsable reutilizable
const Section = ({
  title,
  open,
  onToggle,
  children,
}: {
  title: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}) => (
  <div className="border-b border-line">
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center justify-between py-3 font-serif text-2xl leading-9 text-ink hover:text-clay transition-colors"
    >
      {title}
      <ArrowRightMini
        className={clx("transition-transform duration-150 text-muted", open ? "rotate-90" : "")}
      />
    </button>
    <div className={clx("overflow-hidden transition-all", open ? "max-h-[40rem] pb-3" : "max-h-0")}>
      {children}
    </div>
  </div>
)

const SideMenu = ({ regions, locales, currentLocale }: SideMenuProps) => {
  const countryToggleState = useToggleState()
  const languageToggleState = useToggleState()
  const [openSection, setOpenSection] = useState<"look" | "color" | null>("look")

  const toggle = (s: "look" | "color") =>
    setOpenSection((prev) => (prev === s ? null : s))

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  className="relative h-full flex items-center transition-all ease-out duration-200 focus:outline-none text-ink/80 hover:text-ink font-medium"
                >
                  Menu
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/0 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="flex flex-col absolute w-full pr-4 sm:pr-0 sm:w-1/3 2xl:w-1/4 sm:min-w-min h-[calc(100vh-1rem)] z-[51] inset-x-0 text-sm text-ink m-2">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-bone border border-line rounded-rounded justify-between p-6"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button data-testid="close-menu-button" onClick={close}>
                        <XMark />
                      </button>
                    </div>

                    {/* Cuerpo navegable */}
                    <div className="flex-1 overflow-y-auto -mr-2 pr-2">
                      {/* Buscar — mismo SearchBox del desktop (rutea a /store?q=) */}
                      <div className="pb-4 border-b border-line">
                        <SearchBox className="!w-full" />
                      </div>

                      {/* Home */}
                      <LocalizedClientLink
                        href="/"
                        onClick={close}
                        data-testid="home-link"
                        className="block py-3 border-b border-line font-serif text-2xl leading-9 text-ink hover:text-clay transition-colors"
                      >
                        Home
                      </LocalizedClientLink>

                      {/* Shop by Look */}
                      <Section
                        title="Shop by Look"
                        open={openSection === "look"}
                        onToggle={() => toggle("look")}
                      >
                        <ul className="flex flex-col">
                          {LOOK_CATEGORIES.map((c) => (
                            <li key={c.handle}>
                              <LocalizedClientLink
                                href={`/categories/${c.handle}`}
                                onClick={close}
                                className="flex items-center gap-3 py-2 text-ink hover:text-clay transition-colors group/item"
                              >
                                <span className="w-9 h-9 rounded-lg overflow-hidden ring-1 ring-line shrink-0">
                                  <TileSwatch look={c.look} color={c.color} color2={c.color2} className="h-full w-full" />
                                </span>
                                <span className="font-medium">{c.label}</span>
                              </LocalizedClientLink>
                            </li>
                          ))}
                        </ul>
                      </Section>

                      {/* Shop by Color */}
                      <Section
                        title="Shop by Color"
                        open={openSection === "color"}
                        onToggle={() => toggle("color")}
                      >
                        <ul className="flex flex-col">
                          {COLOR_CHIPS.map((c) => (
                            <li key={c.label}>
                              <LocalizedClientLink
                                href={`/store?color=${encodeURIComponent(c.label)}`}
                                onClick={close}
                                className="flex items-center gap-3 py-2 text-ink hover:text-clay transition-colors"
                              >
                                <span
                                  className="w-6 h-6 rounded-full shrink-0 overflow-hidden"
                                  style={{ backgroundColor: c.swatch, boxShadow: `inset 0 0 0 1.5px ${c.ring}` }}
                                >
                                  {c.split && (
                                    <span
                                      className="block w-full h-full"
                                      style={{ background: `linear-gradient(135deg, ${c.swatch} 0 50%, ${c.split} 50% 100%)` }}
                                    />
                                  )}
                                </span>
                                <span className="font-medium">{c.label}</span>
                              </LocalizedClientLink>
                            </li>
                          ))}
                        </ul>
                      </Section>

                      {/* Links de primer nivel */}
                      <ul className="flex flex-col">
                        {TopLinks.map((link) => (
                          <li key={link.name} className="border-b border-line last:border-0">
                            <LocalizedClientLink
                              href={link.href}
                              onClick={close}
                              data-testid={`${link.name.toLowerCase().replace(/\s+/g, "-")}-link`}
                              className={clx(
                                "block py-3 font-serif text-2xl leading-9 transition-colors",
                                link.accent
                                  ? "text-clay hover:text-clay-deep"
                                  : "text-ink hover:text-clay"
                              )}
                            >
                              {link.name}
                            </LocalizedClientLink>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Footer: idioma / país / copyright */}
                    <div className="flex flex-col gap-y-6 pt-6">
                      {!!locales?.length && (
                        <div
                          className="flex justify-between"
                          onMouseEnter={languageToggleState.open}
                          onMouseLeave={languageToggleState.close}
                        >
                          <LanguageSelect
                            toggleState={languageToggleState}
                            locales={locales}
                            currentLocale={currentLocale}
                          />
                          <ArrowRightMini
                            className={clx(
                              "transition-transform duration-150",
                              languageToggleState.state ? "-rotate-90" : ""
                            )}
                          />
                        </div>
                      )}
                      <div
                        className="flex justify-between"
                        onMouseEnter={countryToggleState.open}
                        onMouseLeave={countryToggleState.close}
                      >
                        {regions && (
                          <CountrySelect
                            toggleState={countryToggleState}
                            regions={regions}
                          />
                        )}
                        <ArrowRightMini
                          className={clx(
                            "transition-transform duration-150",
                            countryToggleState.state ? "-rotate-90" : ""
                          )}
                        />
                      </div>
                      <Text className="flex justify-between txt-compact-small text-muted">
                        © {new Date().getFullYear()} The Tile Place. All rights
                        reserved.
                      </Text>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
